import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { TimelineDataPoint } from '../../types/api';
import { format } from 'date-fns';

interface TimelineChartProps {
  data: TimelineDataPoint[];
  loading?: boolean;
}

export const TimelineChart: React.FC<TimelineChartProps> = ({ data, loading = false }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
        <Typography variant="body2" color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  const chartData = data.map((point) => ({
    timestamp: format(new Date(point.timestamp), 'MMM d HH:mm'),
    events: point.count,
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
        <defs>
          <linearGradient id="eventGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.2} />
            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} vertical={false} />
        <XAxis
          dataKey="timestamp"
          stroke={theme.palette.text.secondary}
          style={{ fontSize: '0.75rem' }}
          tickLine={false}
          interval="preserveStartEnd"
          minTickGap={60}
        />
        <YAxis
          stroke={theme.palette.text.secondary}
          style={{ fontSize: '0.75rem' }}
          tickLine={false}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            borderRadius: 8,
            fontSize: '0.8125rem',
          }}
        />
        <Area
          type="monotone"
          dataKey="events"
          name="Events"
          stroke={theme.palette.primary.main}
          strokeWidth={2}
          fill="url(#eventGradient)"
          dot={false}
          activeDot={{ r: 4, fill: theme.palette.primary.main }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
